import { Activity, AlertTriangle } from 'lucide-react';
import { DashboardStats } from '../types/dashboard';

interface ApiUsageGaugeProps {
  stats: DashboardStats;
  requestLimit: number;
}

export default function ApiUsageGauge({ stats, requestLimit }: ApiUsageGaugeProps) {
  const usagePercent = (stats.requestsThisMonth / requestLimit) * 100;
  const isWarning = usagePercent >= 70;
  const isDanger = usagePercent >= 90;

  // Calculate circle parameters
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (usagePercent / 100) * circumference;

  const getColor = () => {
    if (isDanger) return 'from-red-500 to-red-600';
    if (isWarning) return 'from-amber-500 to-orange-500';
    return 'from-teal-500 to-green-500';
  };

  const getStrokeColor = () => {
    if (isDanger) return '#ef4444';
    if (isWarning) return '#f59e0b';
    return '#14b8a6';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">API Usage</h3>
          <p className="text-sm text-gray-500">Current month</p>
        </div>
        <div className={`bg-gradient-to-br ${getColor()} p-3 rounded-xl`}>
          <Activity className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Circular gauge */}
      <div className="flex flex-col items-center">
        <div className="relative w-48 h-48">
          <svg className="transform -rotate-90 w-48 h-48">
            {/* Background circle */}
            <circle
              cx="96"
              cy="96"
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="96"
              cy="96"
              r={radius}
              stroke={getStrokeColor()}
              strokeWidth="12"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-gray-900">
              {usagePercent.toFixed(0)}%
            </span>
            <span className="text-sm text-gray-500 mt-1">used</span>
          </div>
        </div>

        {/* Stats */}
        <div className="w-full mt-6 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Requests used</span>
            <span className="text-sm font-semibold text-gray-900">
              {stats.requestsThisMonth.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Limit</span>
            <span className="text-sm font-semibold text-gray-900">
              {requestLimit.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Remaining</span>
            <span className={`text-sm font-semibold ${isDanger ? 'text-red-600' : 'text-green-600'}`}>
              {(requestLimit - stats.requestsThisMonth).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Warning message */}
        {isWarning && (
          <div className={`mt-4 w-full flex items-start gap-2 p-3 rounded-lg ${
            isDanger ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
          }`}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              {isDanger
                ? 'You\'re approaching your monthly limit. Consider upgrading to PRO.'
                : 'You\'ve used over 70% of your monthly quota.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
